import * as React from 'react'
import { Logger } from 'src/3rds/cube'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { formatarDia } from '../Dashboards.periodo'
import { DashboardsScope, DLQ_POR_PAGINA } from '../Dashboards.scopes'
import {
    descreverErro,
    formatarDuracao,
    opcaoEntregas,
    opcaoEspera,
    opcaoFalhas,
    resumirErro,
    ROTULO_CANAL,
    rotuloCanal
} from '../Operacao.graficos'
import { Grafico, Numero } from './Componentes'
import { EChart } from './EChart'

const LOG = Logger.get('Operacao-VIEW')

const numero = (n: number) => n.toLocaleString('pt-BR')
const quando = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

const useStyles = makeStyles()((theme) => ({
    blocos: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: theme.spacing(2.5)
    },
    numeros: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing(2)
    },
    lado: {
        display: 'flex',
        gap: theme.spacing(2.5),
        flexWrap: 'wrap',
        '& > *': {
            flex: '1 1 420px',
            minWidth: 0
        }
    },
    card: {
        borderRadius: 14,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
    },
    tituloCard: {
        fontWeight: 600,
        fontSize: 15
    },
    nota: {
        fontSize: 13,
        color: theme.palette.text.secondary
    },
    erro: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        maxWidth: 520,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    cabecalhoTabela: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    alerta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: theme.palette.warning.dark
    }
}))

/** Dashboard de operação técnica (E16): a LIA está entregando as mensagens? */
export function OperacaoPainel({ scope }: { scope: DashboardsScope }) {
    const { classes } = useStyles()
    const operacao = scope.operacao!
    const periodo = operacao.periodo

    const opcoes = React.useMemo(
        () => ({
            entregas: opcaoEntregas(operacao.serie, periodo),
            falhas: opcaoFalhas(operacao.serie, periodo),
            espera: opcaoEspera(operacao.serie, periodo)
        }),
        [operacao, periodo]
    )

    const whatsapp = operacao.totais.find((t) => t.canal === 'whatsapp')
    const dlq = operacao.totais.reduce((soma, t) => soma + t.dlq, 0)
    const falhas = operacao.totais.reduce((soma, t) => soma + t.falhas, 0)
    const erros400 = operacao.totais.reduce((soma, t) => soma + t.erros400, 0)
    const semDetalheAntes = operacao.detalheDesde > periodo.de

    const emitDlqCanal = (evt: React.ChangeEvent<HTMLInputElement>) =>
        scope.onDlqCanalChange(evt.target.value).catch(LOG.caught)
    const emitDlqPagina = (_evt: unknown, pagina: number) => scope.onDlqPaginaChange(pagina).catch(LOG.caught)

    return (
        <Box className={classes.blocos}>
            <Box className={classes.numeros}>
                <Numero
                    titulo="Enviadas pelo WhatsApp"
                    valor={numero(whatsapp?.enviadas ?? 0)}
                    nota="Mensagens entregues à API da Meta no período"
                />
                <Numero
                    titulo="Foram para a DLQ"
                    valor={numero(dlq)}
                    nota="Desistidas depois de todas as tentativas, em todos os canais"
                />
                <Numero
                    titulo="Tentativas com erro"
                    valor={numero(falhas)}
                    nota={`${numero(erros400)} com resposta HTTP 400`}
                />
                <Numero
                    titulo="Erros 400 desde que o servidor subiu"
                    valor={numero(operacao.erros400DesdeOInicio)}
                    nota="Contador que o check_and_restart.sh usa para reiniciar; não conta o 131056"
                />
            </Box>

            <Card className={classes.card}>
                <CardContent>
                    <Typography className={classes.tituloCard}>Fila agora</Typography>
                    <Typography className={classes.nota}>Mensagens ainda não enviadas, por canal.</Typography>
                    {operacao.agora.length === 0 ? (
                        <Typography sx={{ mt: 1.5 }}>Nenhuma mensagem esperando: a fila está em dia.</Typography>
                    ) : (
                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Canal</TableCell>
                                        <TableCell align="right">Pendentes</TableCell>
                                        <TableCell align="right">Em nova tentativa</TableCell>
                                        <TableCell align="right">Processando</TableCell>
                                        <TableCell align="right">Presas</TableCell>
                                        <TableCell align="right">Espera da mais antiga</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {operacao.agora.map((c) => (
                                        <TableRow key={c.canal}>
                                            <TableCell>{rotuloCanal(c.canal)}</TableCell>
                                            <TableCell align="right">{numero(c.pendentes)}</TableCell>
                                            <TableCell align="right">{numero(c.emRetentativa)}</TableCell>
                                            <TableCell align="right">{numero(c.processando)}</TableCell>
                                            <TableCell align="right">
                                                {c.presas > 0 ? (
                                                    <Tooltip title="Em processamento há mais de 5 minutos: o handler pode ter travado">
                                                        <span className={classes.alerta}>
                                                            <WarningAmberIcon fontSize="small" />
                                                            {numero(c.presas)}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    0
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{formatarDuracao(c.maisAntigaSegundos)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Grafico titulo="Mensagens do WhatsApp enviadas por dia">
                <EChart option={opcoes.entregas} ariaLabel="Mensagens do WhatsApp enviadas por dia" />
            </Grafico>

            <Box className={classes.lado}>
                <Grafico titulo="Tentativas com erro por dia" nota="Todos os canais. Cada tentativa conta uma vez.">
                    <EChart option={opcoes.falhas} ariaLabel="Tentativas com erro por dia, erro 400 e outras" />
                </Grafico>
                <Grafico
                    titulo="Tempo até o envio no WhatsApp"
                    nota="Da entrada na fila até a Meta aceitar a mensagem, inclusive a espera do limite de envio."
                >
                    <EChart option={opcoes.espera} ariaLabel="Tempo até o envio no WhatsApp, mediana e 95%" />
                </Grafico>
            </Box>

            <Box className={classes.lado}>
                <Card className={classes.card}>
                    <CardContent>
                        <Typography className={classes.tituloCard}>Por canal no período</Typography>
                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Canal</TableCell>
                                        <TableCell align="right">Enviadas</TableCell>
                                        <TableCell align="right">DLQ</TableCell>
                                        <TableCell align="right">Tentativas com erro</TableCell>
                                        <TableCell align="right">Erro 400</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {operacao.totais.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>Nenhuma mensagem no período.</TableCell>
                                        </TableRow>
                                    )}
                                    {operacao.totais.map((t) => (
                                        <TableRow key={t.canal}>
                                            <TableCell>{rotuloCanal(t.canal)}</TableCell>
                                            <TableCell align="right">{numero(t.enviadas)}</TableCell>
                                            <TableCell align="right">{numero(t.dlq)}</TableCell>
                                            <TableCell align="right">{numero(t.falhas)}</TableCell>
                                            <TableCell align="right">{numero(t.erros400)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Card className={classes.card}>
                    <CardContent>
                        <Typography className={classes.tituloCard}>Erros por código</Typography>
                        <Typography className={classes.nota}>
                            {semDetalheAntes
                                ? `A fila guarda o detalhe dos erros por 30 dias: esta tabela começa em ${formatarDia(
                                      operacao.detalheDesde,
                                      true
                                  )}.`
                                : 'Cada tentativa com erro, agrupada pela resposta da API.'}
                        </Typography>
                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>HTTP</TableCell>
                                        <TableCell>Código Meta</TableCell>
                                        <TableCell>O que é</TableCell>
                                        <TableCell align="right">Tentativas</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {operacao.erros.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>Nenhum erro no período.</TableCell>
                                        </TableRow>
                                    )}
                                    {operacao.erros.map((e) => (
                                        <TableRow key={`${e.http}-${e.codigoMeta}`}>
                                            <TableCell>{e.http ?? '—'}</TableCell>
                                            <TableCell>{e.codigoMeta ?? '—'}</TableCell>
                                            <TableCell>
                                                <Tooltip title={e.exemplo}>
                                                    <span>{descreverErro(e)}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right">{numero(e.qtde)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>

            <Card className={classes.card}>
                {scope.carregandoDlq && <LinearProgress />}
                <CardContent>
                    <Box className={classes.cabecalhoTabela}>
                        <Box>
                            <Typography className={classes.tituloCard}>Mensagens na DLQ</Typography>
                            <Typography className={classes.nota}>
                                Desistidas no período, da mais recente para a mais antiga. A fila apaga as com mais de
                                30 dias.
                            </Typography>
                        </Box>
                        <TextField
                            select
                            size="small"
                            label="Canal"
                            value={scope.dlqCanal}
                            onChange={emitDlqCanal}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {Object.keys(ROTULO_CANAL).map((canal) => (
                                <MenuItem key={canal} value={canal}>
                                    {rotuloCanal(canal)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Desistida em</TableCell>
                                    <TableCell>Canal</TableCell>
                                    <TableCell>Para</TableCell>
                                    <TableCell align="right">Tentativas</TableCell>
                                    <TableCell>Último erro</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.dlq.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5}>Nenhuma mensagem na DLQ no período.</TableCell>
                                    </TableRow>
                                )}
                                {scope.dlq.map((m) => (
                                    <TableRow key={m.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{quando(m.ultimaTentativa)}</TableCell>
                                        <TableCell>{rotuloCanal(m.canal)}</TableCell>
                                        <TableCell>
                                            {m.parceiro ?? (m.dealId ? `Negócio ${m.dealId}` : '—')}
                                            {m.destino && (
                                                <Typography fontSize={12} color="text.secondary">
                                                    {m.destino}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{m.tentativas}</TableCell>
                                        <TableCell>
                                            <Tooltip title={m.erro}>
                                                <div className={classes.erro}>{resumirErro(m.erro)}</div>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.dlqTotal}
                        page={scope.dlqPagina}
                        onPageChange={emitDlqPagina}
                        rowsPerPage={DLQ_POR_PAGINA}
                        rowsPerPageOptions={[DLQ_POR_PAGINA]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${numero(count)}`}
                    />
                </CardContent>
            </Card>

            {operacao.serie.length === 0 && (
                <Alert severity="info">Nenhuma mensagem saiu da fila no período escolhido.</Alert>
            )}
        </Box>
    )
}

export default OperacaoPainel
